import { Directive, ElementRef, Input, OnChanges, SimpleChanges, inject } from '@angular/core';

import { HisBaseGlobalsService } from '../service/base/his-base-globals.service';
import { SvgService } from '../service/svg/svg.service';

/**
 * looks up an image file key in DispatcherProperties.txt.
 *
 * hisImage may be used directly on img-elements or on any element which may accept
 * a dynamically generated img-element as child.
 */
@Directive( {
    selector: '[hisImage]',
    standalone: false
} )
export class HisImageDirective implements OnChanges {
    private hisBaseGlobalsService = inject( HisBaseGlobalsService );
    private el = inject( ElementRef );
    private svgService = inject( SvgService );


    private inlineSVGEnabled = true;

    @Input() title: string;
    @Input() hisImage: string;
    @Input() forceIMG = false;

    ngOnChanges( changes: SimpleChanges ) {
        if ( changes.hisImage ) {

            // The title attribute must be attached to the element declaring the hisImage directive.
            if ( this.title ) {
                this.el.nativeElement.title = this.title;
            }

            let image = this.hisBaseGlobalsService.getImage( this.hisImage );
            if ( !image ) {
                return;
            }

            // If no SVG file or img-Element is forced -> use img-tag
            if ( this.getFileExtension( image ) !== 'svg' || this.forceIMG || !this.inlineSVGEnabled ) {
                this.createOrCompleteImageElement( this.el.nativeElement, image );
            } else {
                this.svgService.getSVG( '/HISinOne/images/' + image ).subscribe( ( svgEl: SVGElement ) => {
                    if ( svgEl ) {
                        svgEl = svgEl.cloneNode( true ) as SVGElement;
                        // only use svg-files with the class 'his-icon'
                        if ( svgEl.classList.contains( 'his-icon' ) ) {
                            if ( this.el.nativeElement.tagName === 'SPAN' || this.el.nativeElement.tagName === 'DIV' ) {
                                // replacing svg-files not working for <img> / <button> / <a> tags
                                this.addOrReplaceSVGElementToElement( svgEl, this.el.nativeElement );
                            } else {
                                this.addSVGElementToElement( svgEl, this.el.nativeElement );
                            }
                            svgEl.setAttribute( 'aria-hidden', 'true' );
                        } else {
                            this.createOrCompleteImageElement( this.el.nativeElement, image );
                        }
                    }
                } );
            }
        }
    }

    /**
     * Find or create the HTML img element, and set all required attributes.
     *
     * @param image the name of the image
     */
    private createOrCompleteImageElement( element: HTMLElement, image: string ) {
        let img: HTMLImageElement = this.findOrCreateImageElement( element );
        img.src = '/HISinOne/images/' + image;
        // We need alt="" to avoid that a screenreader reads the src-attribute
        img.alt = '';
    }

    /**
     * find the img-element to operate on. Create an img-element child, if necessary
     *
     * @param element element this directive is defined on
     */
    private findOrCreateImageElement( element: HTMLElement ): HTMLImageElement {
        // if we are defined on an img-element, use it directly
        if ( element.tagName === 'IMG' ) {
            return element as HTMLImageElement;
        }

        // is the first child-element an img-element for us to use?
        let child = element.firstElementChild;
        if ( child && child.tagName === 'IMG' ) {
            return child as HTMLImageElement;
        }

        // insert an img-element as first child
        let img = element.ownerDocument.createElement( 'img' );
        img.className = 'inlineicon';

        element.insertBefore( img, element.firstChild );
        return img;
    }

    /**
     * add inline SVG to a HTMLElement
     *
     * @param svgEl SVGElement
     * @param el HTMLElement
     */
    private addSVGElementToElement( svgEl: SVGElement, el: HTMLElement ) {

        // replace <img>-tag
        if ( el.tagName === 'IMG' ) {
            // create an outer span with a title attribute because title is not valid on <svg>
            let outerSpan = document.createElement( 'span' );
            outerSpan.appendChild( svgEl );
            this.copyAttributes( el, outerSpan );

            // create an inner span after the <svg>-tag because screenreaders ignore title on outer <span>
            let span = document.createElement( 'span' );
            span.className = 'visibilityOnlyForScreenreader';
            span.innerText = el.title;
            outerSpan.appendChild( span );

            if ( el.parentNode ) {
                el.parentNode.insertBefore( outerSpan, el );
                el.parentNode.removeChild( el );
            } else {
                console.log( 'Oops, no parentNode! Is the image displayed in dependency of a condition involving lazy loaded data?' );
            }
        } else {
            // otherwise add <svg> as child
            svgEl.classList.add( 'inlineicon' );
            el.insertBefore( svgEl, el.firstChild );
        }
    }

    /**
     * add or replace inline SVG to a HTMLElement for <span> / <div> tags
     *
     * @param svgEl SVGElement
     * @param el HTMLElement
     */
    private addOrReplaceSVGElementToElement( svgEl: SVGElement, el: HTMLElement ) {
        // add or replace <svg> as child
        if ( el.firstChild !== null ) {
            el.firstChild.remove();
        }
        svgEl.classList.add( 'inlineicon' );
        el.insertBefore( svgEl, el.firstChild );
    }

    private getFileExtension( filename: string ): string {
        return filename.split( '.' ).pop();
    }

    copyAttributes( source: HTMLElement, destination: Element ) {
        const attributes: NamedNodeMap = source.attributes;
        // A for-of loop does not work with NamedNodeMap because it has no iterator
        for ( let index = 0; index < attributes.length; index++ ) {
            let attribute = attributes[index];
            if ( attribute.name !== 'hisimage' ) {
                destination.setAttribute( attribute.name, attribute.value );
            }
        }
    }
}
